import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = import.meta.env.VITE_BACKEND_URL || "https://api.viadrive.com";
const PUBLIC_DERIV_BASE =
  import.meta.env.VITE_PUBLIC_DERIV_BASE || API_BASE.replace('/api','') + '/photos-derivatives';

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300">
  <rect width="100%" height="100%" fill="#f2f3f5"/>
  <g fill="#c3c7cf">
    <rect x="40" y="40" width="320" height="220" rx="8"/>
    <circle cx="120" cy="120" r="26" fill="#d6dae1"/>
    <path d="M80 220l60-70 40 40 40-50 60 80z" fill="#d6dae1"/>
  </g>
</svg>`);

const FALLBACK_VIDEO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300">
  <rect width="100%" height="100%" fill="#f2f3f5"/>
  <rect x="40" y="70" width="320" height="160" rx="12" fill="#c3c7cf"/>
  <polygon points="190,120 250,150 190,180" fill="#f2f3f5"/>
  <rect x="140" y="240" width="120" height="12" rx="6" fill="#e0e3e8"/>
</svg>`);

const urlFix = (u) => (!u ? "" : u.startsWith("/i/") ? `${API_BASE}${u}` : u);

function getFilenameFromItem(p) {
  return (p.name || p.key || p.objectKey || p.filename || p.originalKey || "").toLowerCase();
}
function getS3KeyFromItem(p) {
  return p.key || p.objectKey || p.originalKey || p.filename || p.name || "";
}
function isImageItem(p) {
  const mime = (p.mime || p.contentType || "").toLowerCase();
  const name = getFilenameFromItem(p);
  if (mime.startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|webp|gif|avif|heic|bmp)$/i.test(name);
}
function isVideoItem(p) {
  const mime = (p.mime || p.contentType || "").toLowerCase();
  const name = getFilenameFromItem(p);
  if (mime.startsWith("video/")) return true;
  return /\.(mp4|webm|mov)$/i.test(name);
}
function isRawItem(p) {
  const name = getFilenameFromItem(p);
  const ct = (p.mime || p.contentType || "").toLowerCase();
  return (
    /\.(dng|arw|cr2|cr3|nef|raf|orf|rw2)$/i.test(name) ||
    /(dng|arw|cr2|cr3|nef|raf|orf|rw2)/.test(ct) ||
    p.isRaw === true
  );
}

function getThumbUrl(p) {
  if (isImageItem(p)) {
    return urlFix(p.wm || p.web || p.thumb || `/i/${p.id}/web-wm`);
  }
  if (isRawItem(p)) {
    const key = getS3KeyFromItem(p);
    if (key) {
      const base = key.replace(/\/?[^/]+$/, (m) => m.replace(/\.[^.]+$/, ''));
      return `${PUBLIC_DERIV_BASE}/${base}/normalized.webp`;
    }
  }
  if (isVideoItem(p)) {
    const candidates = [p.posterUrl, p.poster, p.thumb].map(urlFix).filter(Boolean);
    return candidates[0] || FALLBACK_VIDEO;
  }
  return FALLBACK_IMG;
}

function getOpenUrl(p) {
  if (isImageItem(p)) return `${API_BASE}/i/${p.id}/full-wm`;
  if (isVideoItem(p)) {
    const candidates = [p.src, p.url, p.web, p.original, p.posterUrl, p.poster]
      .map(urlFix)
      .filter(Boolean);
    return candidates[0] || FALLBACK_VIDEO;
  }
  return urlFix(p.url || p.original) || FALLBACK_IMG;
}

export default function PhotosGrid({ items, onSelectionChange = () => {}, selectAllToken = 0, clearSelectionToken = 0 }) {
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [lastMsg, setLastMsg] = useState("");

  const setAndEmit = (nextSet) => { setSel(nextSet); onSelectionChange(Array.from(nextSet)); };
  const toggle = (id) => setAndEmit(((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })(sel));

  useEffect(() => { if (items?.length) setAndEmit(new Set(items.map((p) => p.id))); }, [selectAllToken]); // eslint-disable-line
  useEffect(() => { setAndEmit(new Set()); }, [clearSelectionToken]); // eslint-disable-line

  const cursorFor = (p) => (isVideoItem(p) ? "pointer" : isImageItem(p) ? "zoom-in" : "default");
  const selectedItems = useMemo(() => (!items?.length || sel.size === 0) ? [] : items.filter((p) => sel.has(p.id)), [items, sel]);
  const rawSelectedCount = selectedItems.filter(isRawItem).length;

  async function convertSelectedRaws() {
    try {
      setLoading(true); setLastMsg("");
      const rawKeys = selectedItems.filter(isRawItem).map(getS3KeyFromItem).filter(Boolean);
      if (rawKeys.length === 0) { setLastMsg("Nenhum RAW selecionado."); return; }

      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_BASE}/api/photos/curadoria`,
        { keys: rawKeys },
        { headers: { "content-type": "application/json", Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      const ok = (data?.results || []).filter((r) => r.ok).length;
      const fail = (data?.results || []).length - ok;
      setLastMsg(`Curadoria: ${ok} ok, ${fail} falha(s).`);
    } catch (e) {
      console.error(e?.response?.data || e);
      const msg =
        e?.response?.data?.error ||
        (e?.response?.data?.results || []).find?.((r) => !r.ok)?.error ||
        e?.message || "Erro desconhecido";
      setLastMsg(`Falha: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div style={toolbarWrap}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={convertSelectedRaws}
            disabled={loading || rawSelectedCount === 0}
            style={{ ...pill, background: "#5d398c", color: "#fff", borderColor: "#5d398c" }}
            title="Extrai JPG e JSON de curadoria para os RAWs selecionados"
          >
            {loading ? "Convertendo..." : `Converter RAW selecionado(s)`}
          </button>
          <small style={{ opacity: 0.75 }}>
            {selectedItems.length ? `${selectedItems.length} selecionado(s)` : "—"}
            {rawSelectedCount ? ` · RAWs: ${rawSelectedCount}` : " · Nenhum RAW selecionado."}
            {lastMsg ? ` · ${lastMsg}` : ""}
          </small>
        </div>
      </div>

      <div style={grid}>
        {items.map((p) => {
          const fileLabel = (p.name || p.key || p.objectKey || p.filename || p.originalKey || "").split("/").pop();
          return (
            <figure key={p.id} style={card} title={p.id}>
              <label style={checkWrap}>
                <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} style={{ display: "none" }} />
                <span style={{ ...checkDot, opacity: sel.has(p.id) ? 1 : 0.35 }}>✓</span>
              </label>

              <div style={{ ...thumbBox, cursor: cursorFor(p) }} onClick={() => window.open(getOpenUrl(p), "_blank")}>
                <img
                  src={getThumbUrl(p)}
                  alt=""
                  style={thumbImg}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = isVideoItem(p) ? FALLBACK_VIDEO : FALLBACK_IMG; }}
                />
              </div>

              <figcaption style={foot}>
                <small style={{ color: "#666" }}>
                  {fileLabel || String(p.id).slice(0, 8)}
                  <br /><span style={{ opacity: 0.6 }}>{String(p.id).slice(0, 8)}</span>
                </small>
                <button onClick={() => window.open(getOpenUrl(p), "_blank")} style={pill}
                        title={isVideoItem(p) ? "Abrir vídeo" : isImageItem(p) ? "Abrir imagem (Full WM)" : "Abrir arquivo"}>
                  {isVideoItem(p) ? "Abrir" : "Full"}
                </button>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </>
  );
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 };
const toolbarWrap = { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "8px 4px 12px" };
const card = { margin: 0, position: "relative", borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,.06)", background: "#fff", overflow: "hidden" };
const checkWrap = { position: "absolute", top: 8, left: 8, zIndex: 2 };
const checkDot = { display: "inline-flex", width: 22, height: 22, borderRadius: 6, background: "rgba(93,57,140,.95)", color: "#fff", alignItems: "center", justifyContent: "center", fontSize: 12 };
const thumbBox = { width: "100%", aspectRatio: "4 / 3", background: "#f6f6f7" };
const thumbImg = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const foot = { padding: "6px 10px 10px", display: "flex", alignItems: "center", justifyContent: "space-between" };
const pill = { border: "1px solid #ddd", background: "#f7f7f8", borderRadius: 999, padding: "2px 8px", fontSize: 12, cursor: "pointer" };
